import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../components";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { User, EnvelopeSimple, Phone, Lock, ShieldCheck, Pulse, UploadSimple, Trash } from "@phosphor-icons/react";
import {
  PageHeader, Button, Input, Select, Switch,
  Card, CardHeader, CardTitle, CardBody,
} from "../../components/ui";

function FormUsers() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [formData, setFormData] = useState({
    doubleAuth: false,
    email: "",
    fullName: "",
    phone: "",
    role_id: "",
    etat: "active",
    password: "",
    image: null,
  });

  useEffect(() => {
    getRoles();
    if (id) getUserToUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    if (e.target.type === "file") {
      const file = e.target.files[0];
      if (file) {
        if (file.size > 15 * 1024 * 1024) {
          toast.warning("File size exceeds 15 MB limit");
          e.target.value = "";
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => setFormData((p) => ({ ...p, image: reader.result }));
      }
    } else {
      setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
    }
  };

  const getRoles = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/role/search");
      if (response.data) setRoles(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  const getUserToUpdate = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/users/get/${id}`);
      if (response.data) setFormData(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch user");
    } finally {
      setLoading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = id
        ? await axiosInstance.put(`/users/update/${id}`, formData)
        : await axiosInstance.post("/users/create", formData);
      toast.success(response.data.message);
      navigate(-1);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full p-6 text-start">
      {loading && <Loading />}

      <PageHeader
        title={id ? "Edit user" : "New user"}
        subtitle={id ? "Update account details and access." : "Add a new member to your team."}
        breadcrumbs={[{ label: "Users", to: "/users" }, { label: id ? "Edit" : "New" }]}
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" form="user-form" loading={loading}>
              {id ? "Save changes" : "Create user"}
            </Button>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile photo</CardTitle>
            </CardHeader>
            <CardBody className="flex flex-col items-center">
              <div className="relative">
                <div className="grid h-28 w-28 place-items-center overflow-hidden rounded-full border border-line bg-surface-2">
                  {formData.image ? (
                    <img src={formData.image} alt="Profile" className="h-full w-full object-cover" />
                  ) : (
                    <User size={36} className="text-faint" />
                  )}
                </div>
                <label
                  htmlFor="photo-upload"
                  className="absolute bottom-0 right-0 grid h-8 w-8 cursor-pointer place-items-center rounded-full bg-accent text-accent-fg shadow transition hover:bg-accent-strong"
                >
                  <UploadSimple size={15} />
                  <input id="photo-upload" type="file" className="hidden" onChange={handleChange} accept="image/*" name="image" />
                </label>
              </div>
              {formData.image && (
                <button
                  type="button"
                  onClick={() => setFormData((p) => ({ ...p, image: null }))}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-red-600 transition hover:text-red-700"
                >
                  <Trash size={15} /> Remove photo
                </button>
              )}
              <p className="mt-2 text-xs text-muted">JPG, PNG or GIF · max 15MB</p>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-start gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-line bg-surface-2 text-muted">
                  <ShieldCheck size={20} />
                </span>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-ink">Two-factor authentication</h3>
                    <Switch checked={formData.doubleAuth} onChange={(val) => setFormData((p) => ({ ...p, doubleAuth: val }))} />
                  </div>
                  <p className="mt-1 text-sm text-muted">Secure the account with email-based 2FA.</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Main form */}
        <form id="user-form" onSubmit={save} className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Account details</CardTitle>
            </CardHeader>
            <CardBody className="grid gap-5 md:grid-cols-2">
              <Input label="Full name" icon={User} name="fullName" value={formData.fullName} onChange={handleChange} placeholder="Enter full name" />
              <Input label="Email address" icon={EnvelopeSimple} type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter email address" />
              <Input label="Phone number" icon={Phone} type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Enter phone number" />
              <Select
                label="Role"
                icon={ShieldCheck}
                placeholder="Select a role"
                options={roles.map((r) => ({ value: r.id, label: r.name }))}
                value={formData.role_id || null}
                onChange={(v) => setFormData((p) => ({ ...p, role_id: v || "" }))}
              />
              <Select
                label="Status"
                icon={Pulse}
                options={[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
                value={formData.etat || null}
                onChange={(v) => setFormData((p) => ({ ...p, etat: v || "" }))}
              />
              {!id && (
                <Input label="Password" icon={Lock} type="password" name="password" value={formData.password} onChange={handleChange} placeholder="Enter password" />
              )}
            </CardBody>
          </Card>
        </form>
      </div>
    </div>
  );
}

export default FormUsers;
